import {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {BufferViewLayout} from 'buffers/BufferComps';
import {Sequence} from 'components/Sequence';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {OligoLinearMapState} from 'molbio/maps/MapStates';
import SinglePolymerMapTabState, {
  SinglePolymerMapTabStateAttributes,
} from 'molbio/tabstates/SinglePolymerMapTabState';
import {TabStateDefaults} from 'Navigation';
import {constructOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface OligoLinearMapTabStateAttributes
  extends SinglePolymerMapTabStateAttributes<Sequence, OligoLinearMapState> {
  preferenceKey: string;
  visibilityOptions: vo.VisibilityOptions;
  bufferViewLayout: BufferViewLayout | null;
}

interface OligoLinearMapTabState extends SinglePolymerMapTabState<Sequence, OligoLinearMapState> {
  get<K extends keyof OligoLinearMapTabStateAttributes>(key: K): OligoLinearMapTabStateAttributes[K];
  set<K extends keyof OligoLinearMapTabStateAttributes>(
    attributeName: K,
    value: OligoLinearMapTabStateAttributes[K],
    options?: ModelSetOptions
  ): OligoLinearMapTabState;
  set(obj: Partial<OligoLinearMapTabStateAttributes>): OligoLinearMapTabState;
}

class OligoLinearMapTabState extends SinglePolymerMapTabState<Sequence, OligoLinearMapState> {
  // Show one fewer base per slice to ensure phosphate mod on the last base is visible if present
  override BASES_PER_SLICE_ADJUSTMENT = -1;

  mapState(options: {
    polymer: AaSequence | Sequence;
    visibilityOptions: VisibilityOptions;
  }): OligoLinearMapState {
    return new OligoLinearMapState(options);
  }

  getVisibilityOptions(): VisibilityOptions {
    return this.get('visibilityOptions');
  }

  msVisibilityOptions(): vo.VisibilityOptions {
    return this.get('visibilityOptions').makeLocalSync();
  }

  override defaults(): TabStateDefaults & Partial<OligoLinearMapTabStateAttributes> {
    return {
      ...super.defaults(),
      closeable: false,
      title: 'Sequence Map',
      bufferViewLayout: null,
      visibilityOptions: constructOptions(
        'linear',
        _.compact([
          {base: vo.MAXIMISE, visible: true, editable: false, sync: false},
          {base: vo.ANNOTATIONS, visible: true, editable: true, sync: true},
          {base: vo.PRIMERS, visible: false, editable: false, sync: false},
          {base: vo.PRIMER_BASES, visible: false, editable: false, sync: false},
          {base: vo.CUTSITES, visible: false, editable: false, sync: false},
          {base: vo.COMPLEMENT, visible: false, editable: false, sync: false},
          {base: vo.ORFS, visible: false, editable: false, sync: false},
          {base: vo.SEQUENCE_AXIS, visible: true, editable: true, sync: true},
          BenchlingConfig.MOLBIO_ENABLE_MODIFIED_NUCLEOTIDES
            ? {base: vo.NUCLEOTIDE_STRUCTURE_IMAGES, visible: true, editable: true, sync: true}
            : null,
          {base: vo.OLIGO_PROPERTIES, visible: true, editable: true, sync: true},
          {base: vo.TRANSLATIONS, visible: false, editable: false, sync: false},
          {base: vo.AA_INDICES, visible: false, editable: false, sync: false},
          {base: vo.OVERHANGS, visible: false, editable: false, sync: false},
          {base: vo.DNA, visible: true, editable: false, sync: true},
          {base: vo.AMINO_ACIDS, visible: false, editable: false, sync: false},
          {base: vo.SEARCH_BASES, visible: true, editable: false, sync: true},
          {base: vo.PARTS, visible: false, editable: false},
        ])
      ),
      preferenceKey: 'oligo-linear-map',
    };
  }
}

export default OligoLinearMapTabState;
